import React, { useState } from "react";
import {
  IProfilesGeneralStatistics,
  ProfileFilterCriteriaType,
} from "../../../data-access";
import { Avatar } from "../../../ui";
import "../../../ui/simple-table";
import { DataTablePagination } from "../../../ui/data-table/data-table-pagination/data-table-pagination";
import { paginate } from "../../../ui/data-table/data-table-utils";
import { HiOutlineInboxStack } from "react-icons/hi2";
import "./profile-statistics.scss";
import { Dayjs } from "dayjs";

interface IProfileStatisticsProps {
  isLoading?: boolean;
  filterPeriod: ProfileFilterCriteriaType | null;
  rangeDate: [Dayjs | null, Dayjs | null];
  data: IProfilesGeneralStatistics;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
};

const getRateClass = (rate: number): string => {
  if (rate >= 10) return "rate--good";
  if (rate >= 5) return "rate--warning";
  return "rate--low";
};

export const ProfileStatistics = ({
  isLoading = false,
  data,
}: IProfileStatisticsProps): JSX.Element => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set([0]));

  const toggleRow = (index: number): void => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const handlePagination = (pp: number, requestedPage: number): void => {
    const paginateData = paginate(data.profiles, pp, requestedPage);
    const actualPage =
      paginateData.length > 0
        ? requestedPage
        : Math.ceil(data.profiles.length / pp);
    setPerPage(pp);
    setCurrentPage(actualPage);
    setExpandedRows(new Set([0]));
  };

  const profiles = data?.profiles || [];
  const paginatedProfiles = paginate(profiles, perPage, currentPage);

  // ── Shimmer skeleton ──

  const renderShimmer = () => (
    <div className="simple-table profile-statistics__table">
      <div className="simple-table__header profile-statistics__grid">
        <span className="simple-table__col simple-table__col--member">Profile</span>
        <span className="simple-table__col">Proposals</span>
        <span className="simple-table__col">Replies</span>
        <span className="simple-table__col">Reply %</span>
        <span className="simple-table__col">Invites</span>
        <span className="simple-table__col">DM</span>
        <span className="simple-table__col">Offers</span>
        <span className="simple-table__col simple-table__col--right">Connects</span>
      </div>
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="simple-table__row simple-table__row--expanded profile-statistics__grid">
          <div className="simple-table__col simple-table__col--member" data-label="">
            <div className="profile-statistics__shimmer profile-statistics__shimmer--avatar" />
            <div>
              <div className="profile-statistics__shimmer profile-statistics__shimmer--name" />
              <div className="profile-statistics__shimmer profile-statistics__shimmer--email" />
            </div>
          </div>
          <span className="simple-table__col" data-label="Proposals"><div className="profile-statistics__shimmer profile-statistics__shimmer--cell" /></span>
          <span className="simple-table__col" data-label="Replies"><div className="profile-statistics__shimmer profile-statistics__shimmer--cell" /></span>
          <span className="simple-table__col" data-label="Reply %"><div className="profile-statistics__shimmer profile-statistics__shimmer--cell" /></span>
          <span className="simple-table__col" data-label="Invites"><div className="profile-statistics__shimmer profile-statistics__shimmer--cell" /></span>
          <span className="simple-table__col" data-label="DM"><div className="profile-statistics__shimmer profile-statistics__shimmer--cell" /></span>
          <span className="simple-table__col" data-label="Offers"><div className="profile-statistics__shimmer profile-statistics__shimmer--cell" /></span>
          <span className="simple-table__col simple-table__col--right" data-label="Connects"><div className="profile-statistics__shimmer profile-statistics__shimmer--cell" /></span>
        </div>
      ))}
    </div>
  );

  // ── Render ──

  if (isLoading) return <div className="profile-statistics">{renderShimmer()}</div>;

  if (profiles.length === 0) {
    return (
      <div className="profile-statistics">
        <div className="profile-statistics__empty">
          <HiOutlineInboxStack size={32} />
          <span>No data</span>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-statistics">
      <div className="simple-table profile-statistics__table">
        <div className="simple-table__header profile-statistics__grid">
          <span className="simple-table__col simple-table__col--member">Profile</span>
          <span className="simple-table__col">Proposals</span>
          <span className="simple-table__col">Replies</span>
          <span className="simple-table__col">Reply %</span>
          <span className="simple-table__col">Invites</span>
          <span className="simple-table__col">DM</span>
          <span className="simple-table__col">Offers</span>
          <span className="simple-table__col simple-table__col--right">Connects</span>
        </div>

        {paginatedProfiles.map((profile, index) => {
          const portrait =
            profile.upwork_user.portrait.portrait ||
            profile.upwork_user.portrait.portrait500;

          return (
            <div
              key={index}
              className={`simple-table__row profile-statistics__grid${expandedRows.has(index) ? " simple-table__row--expanded" : ""}`}
              onClick={(e) => {
                const header = e.currentTarget.firstElementChild;
                if (header && header.contains(e.target as Node)) toggleRow(index);
              }}
            >
              <div className="simple-table__col simple-table__col--member" data-label="">
                <div className="profile-cell">
                  {portrait ? (
                    <Avatar imageSrc={portrait} size="small" />
                  ) : (
                    <span className="profile-cell__avatar">
                      {getInitials(profile.upwork_user.username)}
                    </span>
                  )}
                  <div className="profile-cell__info">
                    <span className="profile-cell__name">{profile.upwork_user.username}</span>
                    <span className="profile-cell__email">{profile.email}</span>
                  </div>
                </div>
              </div>
              <span className="simple-table__col simple-table__col--primary" data-label="Proposals">
                {profile.bids}
              </span>
              <span className="simple-table__col simple-table__col--primary" data-label="Replies">
                {profile.replies}
              </span>
              <span className="simple-table__col simple-table__col--primary" data-label="Reply %">
                <span className={`reply-rate ${getRateClass(profile.reply_rate)}`}>
                  {profile.reply_rate.toFixed(1)}%
                </span>
              </span>
              <span className="simple-table__col simple-table__col--primary" data-label="Invites">
                {profile.invites}
              </span>
              <span className="simple-table__col simple-table__col--primary" data-label="DM">
                {profile.direct_messages}
              </span>
              <span className="simple-table__col simple-table__col--primary" data-label="Offers">
                {profile.offers_received}
              </span>
              <span className="simple-table__col simple-table__col--primary simple-table__col--right" data-label="Connects">
                {profile.used_connects}
              </span>
            </div>
          );
        })}

        {/* Totals row */}
        {data?.total && (
          <div className="simple-table__row simple-table__row--expanded profile-statistics__grid profile-statistics__totals">
            <span className="simple-table__col simple-table__col--member profile-statistics__total-label" data-label="">
              Total
            </span>
            <span className="simple-table__col simple-table__col--primary" data-label="Proposals">
              {data.total.bids}
            </span>
            <span className="simple-table__col simple-table__col--primary" data-label="Replies">
              {data.total.replies}
            </span>
            <span className="simple-table__col simple-table__col--primary" data-label="Reply %">
              <span className={`reply-rate ${getRateClass(data.total.reply_rate)}`}>
                {data.total.reply_rate?.toFixed(1)}%
              </span>
            </span>
            <span className="simple-table__col simple-table__col--primary" data-label="Invites">
              {data.total.invites}
            </span>
            <span className="simple-table__col simple-table__col--primary" data-label="DM">
              {data.total.direct_messages}
            </span>
            <span className="simple-table__col simple-table__col--primary" data-label="Offers">
              {data.total.offers_received || 0}
            </span>
            <span className="simple-table__col simple-table__col--primary simple-table__col--right" data-label="Connects">
              {data.total.used_connects || 0}
            </span>
          </div>
        )}

        <DataTablePagination
          perPage={perPage}
          perPageOptions={[10, 25, 50]}
          total={profiles.length}
          currentPage={currentPage}
          onPagination={handlePagination}
        />
      </div>
    </div>
  );
};
